'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import type { ReactNode } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger as ShadcnAccordionTrigger } from '@/components/ui/accordion';
import { cn } from '@/lib/ui/utils';

// Morfi semantic tokens layered over stock accordion.tsx (which uses rounded-md).
const ACCORDION_TRIGGER_TOKENS = 'rounded-item' as const;
type AccordionTriggerProps = React.ComponentProps<typeof ShadcnAccordionTrigger>;

/**
 * Morfi-tokenised AccordionTrigger — applies `rounded-item` (4 px) on top of
 * the stock primitive's `rounded-md` (6 px). This shim guarantees both the
 * flat `<AppAccordion items={…}>` API and the compound `<AccordionTrigger>`
 * re-export below produce identical chrome.
 */
export function AccordionTrigger(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn(ACCORDION_TRIGGER_TOKENS, className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <ShadcnAccordionTrigger className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}

// Re-export compound primitives so consumers may compose the underlying parts
// (mirrors the AppPopover / AppDialog pattern). The flat API below covers the
// common case; the compound API covers everything else.
export { Accordion, AccordionItem, AccordionContent };
export interface AppAccordionItem {
  /** Unique id used as the Radix `value` for the item. */
  id: string;
  /** Trigger label (also used as the visible question). */
  question: ReactNode;
  /** Body content rendered inside the expanded panel. */
  answer: ReactNode;
  /** Optional className applied to the wrapping `AccordionItem`. */
  itemClassName?: string;
  /** Optional className applied to the `AccordionTrigger`. */
  triggerClassName?: string;
  /** Optional className applied to the `AccordionContent`. */
  contentClassName?: string;
}
type AccordionRootProps = React.ComponentProps<typeof Accordion>;
export type AppAccordionType = 'single' | 'multiple';
export interface AppAccordionProps {
  /** Rows to render. Each `id` becomes the Radix item value. */
  items: AppAccordionItem[];
  /**
   * Radix accordion mode. `'multiple'` allows independent rows; `'single'`
   * collapses the previously open row when a new one opens.
   */
  type?: AppAccordionType;
  /**
   * Default open value(s). Tuple for `multiple`, single id for `single`.
   * Mirrors Radix's typing — passing a string array under `single` mode is
   * a developer error caught by Radix at runtime.
   */
  defaultValue?: string | string[];
  /** Controlled open value(s). */
  value?: string | string[];
  /** Controlled change handler. */
  onValueChange?: ((value: string | string[]) => void) | undefined;
  /** Apply collapsed-by-default behavior to `single` mode. Ignored for `multiple`. */
  collapsible?: boolean;
  /** Optional className applied to the root `<Accordion>`. */
  className?: string;
}
interface AppAccordionComponent {
  (props: AppAccordionProps): React.ReactNode;
  /** Compound: root primitive — for callers that need full Radix control. */
  Root: typeof Accordion;
  /** Compound: per-row item. */
  Item: typeof AccordionItem;
  /** Compound: trigger. */
  Trigger: typeof AccordionTrigger;
  /** Compound: content panel. */
  Content: typeof AccordionContent;
}
function AppAccordionImpl(t0) {
  const $ = _c(14);
  const {
    items,
    type: t1,
    defaultValue,
    value,
    onValueChange,
    collapsible,
    className
  } = t0;
  const type = t1 === undefined ? "multiple" : t1;
  let t2;
  if ($[0] !== collapsible || $[1] !== defaultValue || $[2] !== onValueChange || $[3] !== type || $[4] !== value) {
    t2 = type === "multiple" ? {
      type: "multiple",
      ...(defaultValue !== undefined ? {
        defaultValue: Array.isArray(defaultValue) ? defaultValue : [defaultValue]
      } : {}),
      ...(value !== undefined ? {
        value: Array.isArray(value) ? value : [value]
      } : {}),
      ...(onValueChange !== undefined ? {
        onValueChange: onValueChange as (v: string[]) => void
      } : {})
    } satisfies AccordionRootProps : {
      type: "single",
      ...(defaultValue !== undefined ? {
        defaultValue: Array.isArray(defaultValue) ? defaultValue[0] ?? "" : defaultValue
      } : {}),
      ...(value !== undefined ? {
        value: Array.isArray(value) ? value[0] ?? "" : value
      } : {}),
      ...(onValueChange !== undefined ? {
        onValueChange: onValueChange as (v: string) => void
      } : {}),
      ...(collapsible !== undefined ? {
        collapsible
      } : {})
    } satisfies AccordionRootProps;
    $[0] = collapsible;
    $[1] = defaultValue;
    $[2] = onValueChange;
    $[3] = type;
    $[4] = value;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const rootProps = t2;
  let t3;
  if ($[6] !== className) {
    t3 = cn(className);
    $[6] = className;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== items) {
    t4 = items.map(_temp);
    $[8] = items;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== rootProps || $[11] !== t3 || $[12] !== t4) {
    t5 = <Accordion {...rootProps} className={t3}>{t4}</Accordion>;
    $[10] = rootProps;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function _temp(item) {
  return <AccordionItem key={item.id} value={item.id} className={cn(item.itemClassName)}><AccordionTrigger className={cn(item.triggerClassName)}>{item.question}</AccordionTrigger><AccordionContent className={cn(item.contentClassName)}>{item.answer}</AccordionContent></AccordionItem>;
}
export const AppAccordion: AppAccordionComponent = Object.assign(AppAccordionImpl, {
  Root: Accordion,
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent
});